import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Plus, Trash2, Box, DoorOpen, Search, Copy, ChevronRight, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { SITE_URL, buildBreadcrumbLd } from "@/lib/seo";
import { useSeoT, PAGE_META } from "@/lib/seoPagesI18n";
import { useToolsT } from "@/lib/toolsI18n";
import { ROOM_KEYS, CATEGORIES, CONTAINER_TYPES, ITEM_ACTIONS } from "@/lib/toolsConfig";
import { useProject, getInventoryCounts, track } from "@/lib/projectStore";

const H2 = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const uid = (p) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

export default function InventaireDemenagement() {
  const navigate = useNavigate();
  const { t: st, lang } = useSeoT();
  const T = useToolsT(lang);
  const I = T.inventory;
  const meta = (PAGE_META[lang] || PAGE_META.fr).inventaire;
  const { project, update } = useProject(true);
  const inv = project?.inventory || { rooms: [], containers: [], items: [] };
  const [view, setView] = useState("room");
  const [openRoom, setOpenRoom] = useState(null);
  const [itemForm, setItemForm] = useState(null); // {roomId, containerId}
  const [quick, setQuick] = useState({ name: "", qty: 1, roomId: "" });
  const [q, setQ] = useState("");
  const [fRoom, setFRoom] = useState(""); const [fCat, setFCat] = useState(""); const [fFlag, setFFlag] = useState("");

  React.useEffect(() => { track("inventory_start"); }, []);
  const counts = getInventoryCounts(project);

  const addRoom = (key) => update((p) => {
    const name = T.rooms[key] || I.newRoom;
    // avoid duplicate same key room
    p.inventory.rooms.push({ id: uid("r"), key, name });
    return p;
  });
  const delRoom = (id) => update((p) => {
    p.inventory.rooms = p.inventory.rooms.filter((r) => r.id !== id);
    p.inventory.containers = p.inventory.containers.filter((c) => c.roomId !== id);
    p.inventory.items = p.inventory.items.filter((it) => it.roomId !== id);
    return p;
  });
  const addContainer = (roomId, type = "carton") => update((p) => {
    const n = p.inventory.containers.filter((c) => c.type === type).length + 1;
    p.inventory.containers.push({ id: uid("c"), roomId, type, name: `${T.ctypes[type]} ${String(n).padStart(2, "0")}` });
    track("inventory_container_add", { type });
    return p;
  });
  const delContainer = (id) => update((p) => {
    p.inventory.containers = p.inventory.containers.filter((c) => c.id !== id);
    p.inventory.items = p.inventory.items.map((it) => it.containerId === id ? { ...it, containerId: null } : it);
    return p;
  });
  const addItem = (data) => update((p) => {
    p.inventory.items.push({ id: uid("i"), name: data.name, qty: Math.max(1, Number(data.qty) || 1), cat: data.cat || "divers", roomId: data.roomId, containerId: data.containerId || null, fragile: !!data.fragile, heavy: !!data.heavy, special: !!data.special, action: data.action || "demenager", notes: data.notes || "" });
    track("inventory_item_add");
    return p;
  });
  const delItem = (id) => update((p) => { p.inventory.items = p.inventory.items.filter((it) => it.id !== id); return p; });
  const dupItem = (id) => update((p) => { const it = p.inventory.items.find((x) => x.id === id); if (it) p.inventory.items.push({ ...it, id: uid("i") }); return p; });
  const moveItem = (id, roomId, containerId) => update((p) => { const it = p.inventory.items.find((x) => x.id === id); if (it) { it.roomId = roomId; it.containerId = containerId || null; } return p; });
  const setItemField = (id, field, val) => update((p) => { const it = p.inventory.items.find((x) => x.id === id); if (it) it[field] = val; return p; });

  const quickAdd = () => {
    if (!quick.name || !quick.roomId) return;
    addItem({ name: quick.name, qty: quick.qty, roomId: quick.roomId, cat: "divers" });
    setQuick({ name: "", qty: 1, roomId: quick.roomId });
  };

  const roomName = (id) => inv.rooms.find((r) => r.id === id)?.name || "—";
  const containerName = (id) => inv.containers.find((c) => c.id === id)?.name || null;
  const itemsIn = (roomId, containerId) => inv.items.filter((it) => it.roomId === roomId && (containerId ? it.containerId === containerId : !it.containerId));
  const roomCount = (roomId) => inv.items.filter((it) => it.roomId === roomId).reduce((s, it) => s + (Number(it.qty) || 1), 0);

  const filtered = useMemo(() => inv.items.filter((it) => {
    if (q && !it.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (fRoom && it.roomId !== fRoom) return false;
    if (fCat && it.cat !== fCat) return false;
    if (fFlag === "fragile" && !it.fragile) return false;
    if (fFlag === "heavy" && !it.heavy) return false;
    if (fFlag === "special" && !it.special) return false;
    return true;
  }), [inv.items, q, fRoom, fCat, fFlag]);

  const bc = [{ name: st.bc.home, path: "/" }, { name: st.bc.outils, path: "/outils" }, { name: meta.h1, path: "/outils/inventaire-demenagement" }];
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "WebApplication", name: meta.h1, applicationCategory: "UtilitiesApplication", operatingSystem: "Web", url: `${SITE_URL}/outils/inventaire-demenagement`, offers: { "@type": "Offer", price: "0", priceCurrency: "CHF" }, publisher: { "@id": `${SITE_URL}/#organization` } },
    buildBreadcrumbLd(toLdItems(bc)),
  ];
  const goQuote = () => { track("inventory_complete", { items: counts.items }); navigate("/devis?service=move"); };

  const Flags = ({ it }) => (
    <span className="flex flex-wrap gap-1">
      {it.fragile && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 font-bold">{I.fragile}</span>}
      {it.heavy && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-bold">{I.heavy}</span>}
      {it.special && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 font-bold">{I.special}</span>}
    </span>
  );

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="inventaire">
      <Seo title={meta.title} description={meta.desc} path="/outils/inventaire-demenagement" jsonLd={jsonLd} />
      <Header />
      <section className="relative bg-[#0A0F2C] text-white overflow-hidden pt-28 md:pt-36 pb-12 md:pb-16 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.4), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1080px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-3xl md:text-5xl tracking-tighter leading-[1.02] max-w-2xl" style={H2}>{meta.h1}</h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl">{meta.intro}</p>
          <div className="mt-5 flex gap-5 text-sm text-white/70">
            <span><b className="text-white text-lg">{counts.rooms}</b> {I.byRoom.toLowerCase()}</span>
            <span><b className="text-white text-lg">{counts.items}</b> {I.totalItems}</span>
            <span><b className="text-white text-lg">{counts.units}</b> {I.totalUnits}</span>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1080px" }}>
          {/* Quick add */}
          <div className="rounded-3xl bg-[#EAF0FF] p-5 md:p-6" data-testid="inv-quick">
            <div className="font-bold text-sm mb-1" style={H2}>{I.quickTitle}</div>
            <div className="text-xs text-[#4B5563] mb-3">{I.quickHint}</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={quick.name} onChange={(e) => setQuick({ ...quick, name: e.target.value })} placeholder={I.addItem} className="flex-1 rounded-xl border border-white bg-white px-3 py-3 text-sm" data-testid="inv-quick-name" />
              <input type="number" min="1" value={quick.qty} onChange={(e) => setQuick({ ...quick, qty: e.target.value })} className="w-20 rounded-xl border border-white bg-white px-3 py-3 text-sm" data-testid="inv-quick-qty" />
              <select value={quick.roomId} onChange={(e) => setQuick({ ...quick, roomId: e.target.value })} className="rounded-xl border border-white bg-white px-3 py-3 text-sm" data-testid="inv-quick-room">
                <option value="">{I.inRoom}</option>
                {inv.rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button onClick={quickAdd} disabled={!quick.name || !quick.roomId} className="rounded-xl bg-[#2D5BFF] text-white px-5 py-3 font-bold text-sm disabled:opacity-40" data-testid="inv-quick-add"><Plus size={16} className="inline" /> {T.common.add}</button>
            </div>
          </div>

          {/* View toggle */}
          <div className="mt-6 flex items-center justify-between">
            <div className="inline-flex rounded-full bg-[#F7F7F8] p-1" data-testid="inv-view-toggle">
              <button onClick={() => setView("room")} className={`rounded-full px-4 py-2 text-sm font-bold ${view === "room" ? "bg-white shadow-sm text-[#0A0F2C]" : "text-[#6B7280]"}`} data-testid="inv-view-room">{I.byRoom}</button>
              <button onClick={() => setView("global")} className={`rounded-full px-4 py-2 text-sm font-bold ${view === "global" ? "bg-white shadow-sm text-[#0A0F2C]" : "text-[#6B7280]"}`} data-testid="inv-view-global">{I.global}</button>
            </div>
          </div>

          {/* Add room selector */}
          <div className="mt-5 flex flex-wrap gap-2" data-testid="inv-add-rooms">
            {ROOM_KEYS.map((rk) => (
              <button key={rk} onClick={() => addRoom(rk)} className="rounded-full border border-gray-200 hover:border-[#2D5BFF] hover:text-[#2D5BFF] px-3.5 py-2 text-sm font-semibold transition-colors" data-testid={`inv-addroom-${rk}`}><Plus size={13} className="inline mr-1" />{T.rooms[rk]}</button>
            ))}
          </div>

          {inv.rooms.length === 0 && <p className="mt-8 text-center text-[#6B7280] text-sm" data-testid="inv-empty">{I.createFirst}</p>}

          {/* ROOM VIEW */}
          {view === "room" && inv.rooms.length > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="inv-rooms">
              {inv.rooms.map((r) => {
                const isOpen = openRoom === r.id;
                const direct = itemsIn(r.id, null);
                const conts = inv.containers.filter((c) => c.roomId === r.id);
                return (
                  <div key={r.id} className="rounded-3xl border border-gray-100 overflow-hidden" data-testid={`inv-room-${r.id}`}>
                    <button onClick={() => setOpenRoom(isOpen ? null : r.id)} className="w-full flex items-center justify-between gap-3 p-5 hover:bg-[#F7F7F8] transition-colors">
                      <span className="flex items-center gap-3"><DoorOpen size={18} className="text-[#2D5BFF]" /><span className="font-extrabold" style={H2}>{r.name}</span></span>
                      <span className="flex items-center gap-2 text-sm text-[#6B7280]"><span>{roomCount(r.id)} {I.elements}</span><ChevronRight size={16} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} /></span>
                    </button>
                    {isOpen && (
                      <div className="p-5 pt-0">
                        <div className="flex gap-2 mb-3">
                          <button onClick={() => setItemForm({ roomId: r.id, containerId: null })} className="rounded-full bg-[#2D5BFF] text-white px-4 py-2 text-xs font-bold" data-testid={`inv-additem-${r.id}`}><Plus size={12} className="inline" /> {I.addItem}</button>
                          <button onClick={() => addContainer(r.id, "carton")} className="rounded-full bg-[#F7F7F8] px-4 py-2 text-xs font-bold" data-testid={`inv-addcont-${r.id}`}><Box size={12} className="inline" /> {I.addContainer}</button>
                          <button onClick={() => delRoom(r.id)} className="ml-auto rounded-full bg-red-50 text-red-500 px-3 py-2 text-xs font-bold" data-testid={`inv-delroom-${r.id}`}><Trash2 size={12} /></button>
                        </div>
                        {/* direct items */}
                        {direct.map((it) => (
                          <ItemRow key={it.id} it={it} T={T} I={I} inv={inv} onDel={delItem} onDup={dupItem} onMove={moveItem} onField={setItemField} Flags={Flags} />
                        ))}
                        {/* containers */}
                        {conts.map((c) => (
                          <div key={c.id} className="mt-3 rounded-2xl bg-[#F7F7F8] p-3" data-testid={`inv-cont-${c.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="flex items-center gap-2 font-bold text-sm"><Box size={14} className="text-[#2D5BFF]" /> {c.name}</span>
                              <span className="flex gap-1">
                                <button onClick={() => setItemForm({ roomId: r.id, containerId: c.id })} className="rounded-full bg-white px-2.5 py-1 text-xs font-bold" data-testid={`inv-cont-additem-${c.id}`}><Plus size={11} className="inline" /></button>
                                <button onClick={() => delContainer(c.id)} className="rounded-full bg-white text-red-500 px-2.5 py-1 text-xs" data-testid={`inv-cont-del-${c.id}`}><Trash2 size={11} /></button>
                              </span>
                            </div>
                            {itemsIn(r.id, c.id).length === 0 ? <div className="text-xs text-[#9CA3AF] px-1">{I.emptyContainer}</div>
                              : itemsIn(r.id, c.id).map((it) => <ItemRow key={it.id} it={it} T={T} I={I} inv={inv} onDel={delItem} onDup={dupItem} onMove={moveItem} onField={setItemField} Flags={Flags} compact />)}
                          </div>
                        ))}
                        {direct.length === 0 && conts.length === 0 && <div className="text-sm text-[#9CA3AF]">{I.emptyRoom}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* GLOBAL VIEW */}
          {view === "global" && (
            <div className="mt-6" data-testid="inv-global">
              <div className="flex flex-col md:flex-row gap-2 mb-4">
                <div className="flex-1 relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={I.searchPh} className="w-full rounded-xl border border-gray-200 pl-9 pr-3 py-2.5 text-sm" data-testid="inv-search" /></div>
                <select value={fRoom} onChange={(e) => setFRoom(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="inv-filter-room"><option value="">{I.filterRoom}: {T.common.all}</option>{inv.rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
                <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="inv-filter-cat"><option value="">{I.filterCat}: {T.common.all}</option>{CATEGORIES.map((c) => <option key={c} value={c}>{T.cats[c]}</option>)}</select>
                <select value={fFlag} onChange={(e) => setFFlag(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="inv-filter-flag"><option value="">{T.common.all}</option><option value="fragile">{I.fragile}</option><option value="heavy">{I.heavy}</option><option value="special">{I.special}</option></select>
              </div>
              {filtered.length === 0 ? <p className="text-center text-[#9CA3AF] text-sm py-8">{I.noResults}</p> : (
                <div className="rounded-2xl border border-gray-100 divide-y divide-gray-100">
                  {filtered.map((it) => (
                    <div key={it.id} className="flex items-center justify-between gap-3 p-3.5" data-testid={`inv-gitem-${it.id}`}>
                      <div className="min-w-0"><div className="font-semibold text-[15px] truncate">{it.name} <span className="text-[#9CA3AF] font-normal">×{it.qty}</span></div><div className="text-xs text-[#9CA3AF] flex items-center gap-2 mt-0.5">{roomName(it.roomId)}{containerName(it.containerId) ? ` · ${containerName(it.containerId)}` : ""} <Flags it={it} /></div></div>
                      <div className="flex gap-1 shrink-0"><button onClick={() => dupItem(it.id)} className="w-8 h-8 rounded-full bg-[#F7F7F8] inline-flex items-center justify-center" aria-label="dup"><Copy size={13} /></button><button onClick={() => delItem(it.id)} className="w-8 h-8 rounded-full bg-red-50 text-red-500 inline-flex items-center justify-center" aria-label="del"><Trash2 size={13} /></button></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          {counts.items > 0 && (
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 rounded-3xl bg-[#0A0F2C] text-white p-7">
              <div className="flex-1"><div className="font-extrabold text-xl" style={H2}>{I.cta}</div></div>
              <button onClick={goQuote} className="rounded-full bg-[#FFD84D] text-[#0A0F2C] px-7 py-4 font-extrabold text-sm inline-flex items-center gap-2" data-testid="inv-cta-quote">{T.summary.quote} <ArrowRight size={15} /></button>
            </div>
          )}
        </div>
      </section>

      {/* Item form modal */}
      {itemForm && (
        <ItemFormModal T={T} I={I} inv={inv} ctx={itemForm} onClose={() => setItemForm(null)} onAdd={(data) => { addItem(data); setItemForm(null); }} />
      )}
      <Footer />
    </div>
  );
}

function ItemRow({ it, T, I, inv, onDel, onDup, onMove, onField, Flags, compact }) {
  const [openMove, setOpenMove] = useState(false);
  const targets = inv.rooms.flatMap((r) => [{ roomId: r.id, containerId: null, label: r.name }, ...inv.containers.filter((c) => c.roomId === r.id).map((c) => ({ roomId: r.id, containerId: c.id, label: `${r.name} · ${c.name}` }))]);
  return (
    <div className={`flex items-center justify-between gap-2 ${compact ? "py-2" : "py-2.5 border-b border-gray-100 last:border-0"}`} data-testid={`inv-item-${it.id}`}>
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate">{it.name} <span className="text-[#9CA3AF] font-normal">×{it.qty}</span></div>
        <div className="flex items-center gap-2 mt-0.5"><Flags it={it} />
          <select value={it.action} onChange={(e) => onField(it.id, "action", e.target.value)} className="text-[11px] text-[#6B7280] bg-transparent border-none p-0 pr-4" data-testid={`inv-item-action-${it.id}`}>
            {["demenager", "garder", "donner", "vendre", "recycler", "jeter"].map((a) => <option key={a} value={a}>{T.actions[a]}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-1 shrink-0 relative">
        <button onClick={() => setOpenMove((v) => !v)} className="w-8 h-8 rounded-full bg-[#F7F7F8] inline-flex items-center justify-center text-xs font-bold" aria-label="move" data-testid={`inv-item-move-${it.id}`}><ArrowRight size={13} /></button>
        {openMove && (
          <div className="absolute right-0 top-9 z-10 bg-white shadow-lg rounded-xl border border-gray-100 p-1 w-52 max-h-52 overflow-auto">
            {targets.map((t, i) => (
              <button key={i} onClick={() => { onMove(it.id, t.roomId, t.containerId); setOpenMove(false); }} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[#F7F7F8] rounded-lg">{t.label}</button>
            ))}
          </div>
        )}
        <button onClick={() => onDup(it.id)} className="w-8 h-8 rounded-full bg-[#F7F7F8] inline-flex items-center justify-center" aria-label="dup"><Copy size={13} /></button>
        <button onClick={() => onDel(it.id)} className="w-8 h-8 rounded-full bg-red-50 text-red-500 inline-flex items-center justify-center" aria-label="del"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

function ItemFormModal({ T, I, inv, ctx, onClose, onAdd }) {
  const [f, setF] = useState({ name: "", qty: 1, cat: "meuble", roomId: ctx.roomId, containerId: ctx.containerId, fragile: false, heavy: false, special: false, notes: "" });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="inv-item-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><div className="font-extrabold text-lg" style={H2}>{I.addItem}</div><button onClick={onClose} aria-label="close"><X size={20} /></button></div>
        <input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={T.common.name} className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm mb-3" data-testid="inv-form-name" />
        <div className="flex gap-2 mb-3">
          <input type="number" min="1" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} className="w-24 rounded-xl border border-gray-200 px-3 py-3 text-sm" data-testid="inv-form-qty" />
          <select value={f.cat} onChange={(e) => setF({ ...f, cat: e.target.value })} className="flex-1 rounded-xl border border-gray-200 px-3 py-3 text-sm" data-testid="inv-form-cat">{CATEGORIES.map((c) => <option key={c} value={c}>{T.cats[c]}</option>)}</select>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {["fragile", "heavy", "special"].map((flag) => (
            <button key={flag} onClick={() => setF({ ...f, [flag]: !f[flag] })} className={`rounded-full px-3.5 py-2 text-xs font-bold transition-all ${f[flag] ? "bg-[#2D5BFF] text-white" : "bg-[#F7F7F8] text-[#6B7280]"}`} data-testid={`inv-form-${flag}`}>{I[flag]}</button>
          ))}
        </div>
        <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder={T.common.notes} className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm mb-4" data-testid="inv-form-notes" />
        <button onClick={() => f.name && onAdd(f)} disabled={!f.name} className="w-full rounded-full bg-[#2D5BFF] text-white px-5 py-3.5 font-bold text-sm disabled:opacity-40" data-testid="inv-form-submit">{T.common.add}</button>
      </div>
    </div>
  );
}
